// announceSessionChange() — the global self-login "announce" primitive
// (plans/2026-07-09--self-login-surface-session-announce.md).
//
// Deliberately dependency-light: it imports NO heavy code (no session_state.js, no signal
// emitter) so global.js can expose it on the ~11KB universal bundle at ~zero weight. It reads
// the shared SessionStateDetector off the namespace at CALL time (not import time), so the
// heavy detector stays only in the packs that boot it (nav.js for the shell; the Rankometer
// component for the standalone native review webview).
//
// A self-login surface (rankometer review submit, ferry MemberOfferAcceptor, AccountPanel via
// the AuthClient) calls this whenever it has authenticated somebody. The detector re-reads the
// cookie truth and, on a real transition, does two things:
//
//   • dispatches the in-page Person-sessionChanged event, so any login-aware component on the
//     page re-renders without a refresh, and
//   • emits the Person-loggedIn/-loggedOut native signal, so the native fan-out pokes every
//     other live webview.
//
// EVERY PLATFORM, not just native. The two consequences above are cleanly separated inside the
// detector: the native emit is guarded by the presence of a signalEmitter (which only native
// surfaces supply — `emitForTransition` returns early without one), while the in-page broadcast
// fires regardless. So the in-page half works on web at no risk of a bogus native signal, and
// gating the whole call on isNativeApp() only ever suppressed the half the web wants. (It also
// forced every host to follow the announce with its own detector.check() to paint itself — a
// workaround this ungating removes.)
//
// If no detector is booted, the call is a safe no-op — which is what the plain website does
// today, until something there boots one.
import { getRankersNamespace } from "../constants/rankers_namespace";

export function announceSessionChange() {
  const detector = getRankersNamespace().sessionStateDetector;
  if (detector) {
    detector.check();
    return;
  }
  // Dev-only regression tripwire (esbuild folds process.env.NODE_ENV to a literal, so this
  // whole branch is dead-code-eliminated from production bundles). announceSessionChange() is
  // only ever called AFTER an auth event, so reaching here means "someone just logged in/out
  // and NO SessionStateDetector is booted on this page" — nothing will emit Person-sessionChanged
  // and session-aware components (e.g. the person nav) will not update. That is the silent
  // failure this primitive's no-op contract otherwise hides; every self-login surface must boot
  // a detector on the platform it runs on (plans/2026-09-12--rankometer-web-session-detector-boot.md).
  // The no-op is preserved deliberately — we warn, we do NOT throw (fire-and-forget contract,
  // unlike observeSessionState's fail-loud shim).
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "[announceSessionChange] a session change was announced but no SessionStateDetector is " +
        "booted on this page — Person-sessionChanged will not fire and session-aware components " +
        "(e.g. the person nav) will not update. A self-login surface on this page must boot a " +
        "detector (see doc/README--native_app_integration.md → 'Self-login surfaces MUST announce')."
    );
  }
}
